import { Award, Users, Globe, Heart, Scale, Building2, Star, GraduationCap, Briefcase } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import danielAlbertPhoto from "@/assets/daniel-albert-attorney.webp";
import { useLanguage } from "@/hooks/useLanguage";
import SEO from "@/components/SEO";
import AIReadableSection from "@/components/AIReadableSection";
import { attorneySchemaEnhanced, organizationSchemaEnhanced, generateFAQSchema } from "@/lib/seo-schemas-enhanced";

// About page FAQs for SEO
const aboutFAQs = [
  {
    question: "What legal practice areas does Daniel Albert Law Firm specialize in?",
    answer: "Daniel Albert Law Firm provides comprehensive legal services in 8 practice areas: Immigration Law, Criminal Defense, Personal Injury, Family Law, Business Law, Real Estate Law, Tax Law, and Probate & Estate Planning."
  },
  {
    question: "What languages does the firm offer services in?",
    answer: "The firm offers trilingual legal services in English, Spanish, and Vietnamese to serve Houston's diverse community."
  },
  {
    question: "What is Attorney Daniel Albert's educational background?",
    answer: "Attorney Daniel Albert holds a J.D. cum laude from North Carolina Central University, an LL.M. in Taxation from Northwestern University, an MBA from University of New Orleans, a B.S. in Biomedical Engineering from Tulane University, and an MRED from Auburn University."
  },
  {
    question: "How much experience does the firm have?",
    answer: "Attorney Daniel Albert has over 20 years of legal experience, having practiced at prestigious international law firms in New York City, Washington D.C., and Jacksonville, Florida, and clerked at the United States Tax Court."
  }
];

// AI-readable summary points
const aiSummaryPoints = [
  "Houston law firm with 20+ years of legal experience",
  "Attorney Daniel F. Albert - J.D., LL.M. in Taxation, MBA, MRED",
  "Federal Tax Court clerkship experience",
  "Practice at international law firms in NYC, DC, and Florida",
  "Trilingual services: English, Spanish, Vietnamese",
  "8 practice areas covering immigration, criminal, injury, family, business, real estate, tax, and probate"
];

const AboutUs = () => {
  const { t, language } = useLanguage();
  
  // Combined schemas for About page
  const aboutSchemas = [attorneySchemaEnhanced, organizationSchemaEnhanced, generateFAQSchema(aboutFAQs)];

  const values = [
    {
      icon: Award,
      title: t('about.values.excellence'),
      description: t('about.values.excellence.desc'),
    },
    {
      icon: Users,
      title: t('about.values.client'),
      description: t('about.values.client.desc'),
    },
    {
      icon: Globe,
      title: t('about.values.access'),
      description: t('about.values.access.desc'),
    },
    {
      icon: Heart,
      title: t('about.values.integrity'),
      description: t('about.values.integrity.desc'),
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="About Attorney Daniel Albert | Houston Immigration & Legal Services"
        description="Meet Attorney Daniel F. Albert - 20+ years of legal experience, LL.M. in Taxation from Northwestern, trilingual services in English, Spanish, and Vietnamese. Houston's trusted law firm."
        schema={aboutSchemas}
      />
      <Header />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-hero text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{t('about.title')}</h1>
            <p className="text-xl opacity-95">
              {t('about.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Attorney Profile Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={danielAlbertPhoto}
                alt="Attorney Daniel F. Albert"
                className="rounded-lg shadow-xl w-full"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6">Attorney Daniel F. Albert</h2>
              <div className="space-y-4 text-lg text-muted-foreground">
                <p>
                  Attorney Daniel F. Albert brings over 20 years of legal expertise and knowledge to his general practice, which consists of immigration, family law, business law, criminal law, personal injury, probate, estate planning, tax law, and real estate.
                </p>
                <p>
                  Daniel practiced for many years as an accomplished lawyer at prestigious international law firms in New York City, Washington, D.C., and Jacksonville, Florida, and clerked for a federal judge at the United States Tax Court. He decided to start his own private law practice when he realized that the valuable legal skills that he had developed over the years of practice could be put to use to help the people of his community, vulnerable adults and families, navigate the challenging legal complexity of everyday life.
                </p>
                <p>
                  Daniel understands how frustrating it can be having to face a legal difficulty and the need to find someone knowledgeable and dependable to help fight through the process. He knows that he cannot help his clients solve all their problems, but wants to put their minds at rest about their legal concerns so that they can focus on their own needs and those of their families. Having seen the impact on clients of poor legal representation, Daniel offers the most common legal services to meet the everyday needs of his clients.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Education Timeline Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">Education & Professional Development</h2>
              <p className="text-xl text-muted-foreground">
                A comprehensive foundation of legal expertise and specialized training
              </p>
            </div>

            <div className="relative">
              {/* Timeline line */}
              <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-accent via-accent to-accent/50"></div>

              {/* Timeline items */}
              <div className="space-y-12">
                {[
                  {
                    year: "1999",
                    degree: "Bachelor of Science in Biomedical Engineering",
                    institution: "Tulane University School of Engineering",
                    location: "New Orleans, LA",
                    type: "education"
                  },
                  {
                    year: "2002",
                    degree: "Master of Business Administration (MBA)",
                    detail: "Concentration in Finance and Accounting",
                    institution: "University of New Orleans",
                    location: "New Orleans, Louisiana",
                    type: "education"
                  },
                  {
                    year: "2006",
                    degree: "Doctor of Jurisprudence (J.D.) cum laude",
                    institution: "North Carolina Central University School of Law",
                    location: "Durham, North Carolina",
                    type: "education"
                  },
                  {
                    year: "2007",
                    degree: "Master of Laws in Taxation (LL.M.)",
                    institution: "Northwestern University Pritzker School of Law and Kellogg School of Management",
                    location: "Chicago, Illinois",
                    type: "education"
                  },
                  {
                    year: "2017",
                    degree: "Master of Real Estate Development (MRED)",
                    institution: "Auburn University Raymond J. Harbert College of Business and The College of Architecture, Design and Construction",
                    location: "Auburn, Alabama",
                    type: "education"
                  },
                  {
                    year: "2018",
                    degree: "Graduate, United States Army Judge Advocate General Corps",
                    detail: "JAG Officer Basic Course",
                    institution: "U.S. Army JAG School, University of Virginia School of Law",
                    location: "Charlottesville, Virginia",
                    type: "certificate"
                  },
                  {
                    year: "2021",
                    degree: "Graduate, Trial Lawyers College",
                    institution: "Trial Lawyers College",
                    location: "Estes Park, Colorado",
                    type: "certificate"
                  },
                  {
                    year: "2021",
                    degree: "Graduate, Basic Trial School",
                    institution: "National Institute of Trial Advocacy (NITA)",
                    location: "",
                    type: "certificate"
                  }
                ].map((item, index) => (
                  <div key={index} className={`relative flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    {/* Timeline dot */}
                    <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full bg-accent border-4 border-background z-10"></div>
                    
                    {/* Content card */}
                    <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                      <div className="bg-card p-6 rounded-lg shadow-lg border border-accent/20 hover:border-accent/50 transition-all hover:shadow-xl">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-gold-shine text-primary font-bold flex items-center justify-center shadow-gold">
                            {item.type === "education" ? <GraduationCap className="h-6 w-6" /> : <Award className="h-6 w-6" />}
                          </div>
                          <div className="text-2xl font-bold text-accent">{item.year}</div>
                        </div>
                        <h3 className="text-xl font-bold mb-2">{item.degree}</h3>
                        {item.detail && (
                          <p className="text-sm text-muted-foreground mb-2 italic">{item.detail}</p>
                        )}
                        <p className="text-muted-foreground font-semibold">{item.institution}</p>
                        {item.location && (
                          <p className="text-sm text-muted-foreground mt-1">{item.location}</p>
                        )}
                      </div>
                    </div>

                    {/* Empty space for the other side on desktop */}
                    <div className="hidden md:block w-5/12"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Experience Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Professional Experience</h2>
              <p className="text-xl text-muted-foreground">
                Over two decades of distinguished legal practice
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6 bg-card rounded-lg shadow-md">
                <div className="h-16 w-16 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-4 shadow-gold">
                  <Briefcase className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-3xl font-bold text-accent mb-2">20+</h3>
                <p className="text-muted-foreground">Years of Legal Experience</p>
              </div>

              <div className="text-center p-6 bg-card rounded-lg shadow-md">
                <div className="h-16 w-16 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-4 shadow-gold">
                  <Building2 className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-3xl font-bold text-accent mb-2">NYC, DC, FL</h3>
                <p className="text-muted-foreground">Prestigious International Law Firms</p>
              </div>

              <div className="text-center p-6 bg-card rounded-lg shadow-md">
                <div className="h-16 w-16 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-4 shadow-gold">
                  <Scale className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-3xl font-bold text-accent mb-2">Federal</h3>
                <p className="text-muted-foreground">U.S. Tax Court Clerkship</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{t('about.values')}</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {t('about.values.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center group">
                <div className="h-20 w-20 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-6 shadow-gold group-hover:scale-110 transition-transform">
                  <value.icon className="h-10 w-10 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-2">{value.title}</h3>
                <p className="text-muted-foreground">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Commitment Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">{t('about.commitment')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-card p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3">{t('about.commitment.personal')}</h3>
                <p className="text-muted-foreground">
                  {t('about.commitment.personal.desc')}
                </p>
              </div>
              <div className="bg-card p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3">{t('about.commitment.communication')}</h3>
                <p className="text-muted-foreground">
                  {t('about.commitment.communication.desc')}
                </p>
              </div>
              <div className="bg-card p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3">{t('about.commitment.representation')}</h3>
                <p className="text-muted-foreground">
                  {t('about.commitment.representation.desc')}
                </p>
              </div>
              <div className="bg-card p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3">{t('about.commitment.available')}</h3>
                <p className="text-muted-foreground">
                  {t('about.commitment.available.desc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-8">{t('about.credentials')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-card p-6 rounded-lg shadow-md hover:shadow-xl transition-all">
                <div className="h-16 w-16 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-4 shadow-gold">
                  <Scale className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold mb-2">{t('about.credentials.licensed')}</h3>
                <p className="text-sm text-muted-foreground">{t('about.credentials.bar')}</p>
              </div>
              <div className="bg-card p-6 rounded-lg shadow-md hover:shadow-xl transition-all">
                <div className="h-16 w-16 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-4 shadow-gold">
                  <Building2 className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold mb-2">{t('about.credentials.aila')}</h3>
                <p className="text-sm text-muted-foreground">{t('about.credentials.aila.full')}</p>
              </div>
              <div className="bg-card p-6 rounded-lg shadow-md hover:shadow-xl transition-all">
                <div className="h-16 w-16 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-4 shadow-gold">
                  <Star className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold mb-2">{t('about.credentials.track')}</h3>
                <p className="text-sm text-muted-foreground">{t('about.credentials.cases')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI-Readable Quick Summary Section */}
      <AIReadableSection 
        title={language === 'es' ? 'Resumen del Abogado' : language === 'vi' ? 'Tóm Tắt Luật Sư' : 'Attorney Summary'} 
        summary={aiSummaryPoints}
      />

      <CTASection
        title="Ready to Work With Us?"
        description="Experience the difference that dedicated, personalized legal representation can make in your case."
      />

      <Footer />
    </div>
  );
};

export default AboutUs;
